<template>
  <portal to="dialog-collection">
    <transition name="el-fade-in-linear">
      <div v-if="visibleIndex > -1" class="dialog-collection" :class="{ 'is-mobile': $store.getters.isMobile }" :style="getStyle" @click="handleMaskClick">
        <bg-style class="dialog-collection__content" :radius="currentDialog.radius" :bg="currentDialog.background">
          <div ref="swiper" class="swiper relative" :class="{ 'overflow-hidden': !editing }" @click="handleSwiperClick">
            <div class="swiper-wrapper slide-block">
              <div v-for="(item, itemIndex) in currentDialog.children" :key="item.id" class="swiper-slide">
                <div class="wrapper" @click="handleNavTo(item)">
                  <rich-text v-if="titleVisible" :ref="`title-${itemIndex}`" v-model="item.title" class="swiper-no-swiping title mb-5" no-init placement="bottom" :disabled="!editing" :editing="editing" />
                  <div v-if="item.list.length" class="image-list relative mb-5">
                    <div v-for="(block, blockIndex) in item.list" :key="blockIndex + 'block'" class="item">
                      <div v-if="block.background.backgroundImageShow" class="relative min-h-[60px]" :class="{ 'min-w-[140px]': editing && !$store.getters.isMobile }">
                        <img class="thumb mx-auto block" :src="block.background.backgroundImage" alt="">
                        <cover-block v-if="editing" absolute class="top-0" @click="handleShowEditImage(itemIndex, blockIndex)" />
                      </div>
                      <rich-text :ref="`picDesc-${blockIndex}`" v-model="block.describe" no-init class="describe mt-2 swiper-no-swiping" force-wrap :editing="editing" :disabled="!editing" />
                    </div>
                  </div>
                  <rich-text v-if="describeVisible" :ref="`desc-${itemIndex}`" v-model="item.describe" class="swiper-no-swiping subtitle" no-init :disabled="!editing" :editing="editing" />
                </div>
              </div>
            </div>
            <div class="swiper-pagination relative" />
            <template v-if="currentDialog.children.length > 1">
              <div class="swiper-button-next"></div>
              <div class="swiper-button-prev"></div>
            </template>
          </div>
          <div v-if="currentDialog.buttonVisible" class="text-center py-5 relative">
            <site-button class="min-w-[80px]">{{ currentDialog.buttonName }}</site-button>
          </div>
        </bg-style>
      </div>
    </transition>
  </portal>
</template>

<script>
import { Pagination, Swiper, Navigation } from 'swiper'
import 'swiper/swiper-bundle.min.css'
import { debounce } from 'lodash-es';
import RichText from '~/components/richText/index.vue'
import { checkNavDataIsRight, navTo, sleep } from '~/utils'
import CoverBlock from '~/components/common/CoverBlock.vue'
import { showImageSetBox } from '~/site/model/helper'
import { WidgetModelNames } from '~/enums/siteEnum'
import SiteButton from '~/site/components/forms/button.vue'

export default {
  components: { SiteButton, CoverBlock, RichText },
  props: {
    model: Object,
    editing: Boolean,
    device: String,
    site: Object
  },
  data() {
    return {
      activeIndex: 0,
      debounceUpdate: null
    }
  },
  computed: {
    getStyle() {
      return {
        '--dialog-radius': this.currentDialog.radius + 'px'
      }
    },
    visibleIndex() {
      const dialogCollection = this.site.widgetList.find(item => item.name === WidgetModelNames.DialogCollectionWidgetModel) || { children: [] }
      return dialogCollection.children.findIndex(item => item.id === this.$store.state.site.currentVisibleDialogId)
    },
    currentDialog() {
      return this.model.children[this.visibleIndex] || {}
    },
    titleVisible() {
      return this.currentDialog.titleVisible
    },
    describeVisible() {
      return this.currentDialog.describeVisible
    }
  },
  watch: {
    visibleIndex(newVal) {
      if (newVal > -1) {
        this.init()
      }
    },
    'currentDialog.children': {
      deep: true,
      handler() {
        this.updateSwiperHeight()
      }
    },
    titleVisible(newVal) {
      newVal && this.initBlockRichText()
    },
    describeVisible(newVal) {
      newVal && this.initBlockRichText()
    },
    async device() {
      await sleep(500)
      await this.init()
    }
  },
  mounted() {
    const that = this
    this.model.__refresh = debounce(that.refresh, 500)
    this.model.__refreshRichText = debounce(that.initBlockRichText, 500)
  },
  methods: {
    handleSwiperClick(e) {
      if (!this.editing) {
        e.stopPropagation()
      }
    },
    handleNavTo(item) {
      if (this.editing || !checkNavDataIsRight(item)) {
        return
      }
      this.$store.commit('site/SET_CURRENT_VISIBLE_DIALOG_ID', null)
      this.$nextTick(() => {
        navTo(this, item)
      })
    },
    handleMaskClick() {
      if (this.editing) return
      this.$store.commit('site/SET_CURRENT_VISIBLE_DIALOG_ID', null)
    },
    refresh() {
      this.initSwiper()
    },
    async updateSwiperHeight() {
      await sleep(0)
      this.swiper?.updateAutoHeight(0)
    },
    async init() {
      await this.initSwiper()
      await this.initBlockRichText()
    },
    async initBlockRichText() {
      await sleep(200)
      const children = this.currentDialog?.children?.[this.activeIndex] || []
      await this.$refs[`title-${this.activeIndex}`]?.[0]?.initEditor()
      await this.$refs[`desc-${this.activeIndex}`]?.[0]?.initEditor()
      children?.list?.forEach((item, index) => {
        this.$refs[`picDesc-${index}`]?.[this.activeIndex]?.initEditor()
      })
    },
    handleShowEditImage(parentIndex, childIndex) {
      const eventKey = `show-dialog-collection-img-${parentIndex}-${childIndex}`
      showImageSetBox(this, eventKey, 2)
    },
    async initSwiper() {
      if (!this.currentDialog?.children?.length) return
      await sleep(200)
      this.swiper?.destroy(true)
      Swiper.use([Pagination, Navigation])
      const that = this
      this.swiper = new Swiper(this.$refs.swiper, {
        preventClicks: false,
        loop: false,
        updateOnWindowResize: false,
        resistanceRatio: 0,
        direction: 'horizontal',
        autoHeight: true,
        modules: [Pagination, Navigation],
        allowTouchMove: false,
        navigation: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next',
        },
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true
        },
        autoplay: false,
        on: {
          init() {
            that.activeIndex = this.activeIndex
            that.initBlockRichText()
          },
          slideChangeTransitionEnd() {
            that.activeIndex = this.activeIndex
            that.initBlockRichText()
          }
        }
      })
    },
  }
}
</script>

<style lang="less">
.dialog-collection {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.5);
  &__content {
    position: absolute !important;
    margin: auto;
    bottom: 0;
    top: 5vh;
    left: 0;
    right: 0;
    z-index: 3010;
    width: 1280px;
    height: fit-content;
    --swiper-navigation-size: 30px;
    .site-button {
      border-radius: var(--dialog-radius);
    }
    .swiper-slide {
      .title,.subtitle {
       padding: 0 30px;
      }
      .wrapper {
        padding: 20px;
        .image-list {
          display: flex;
          flex-wrap: nowrap;
          padding: 0 30px;
          gap: 30px;
          .item {
            flex: 1;
            max-width: 100%;
          }
        }
      }
    }
  }
  &.is-mobile {
    .dialog-collection__content {
      width: 100%;
      .swiper-slide {
        .title,.subtitle {
          padding: 0 16px;
        }
      }
      .wrapper {
        padding: 20px 0;
        .image-list {
          padding: 0 16px;
          gap: 10px;
        }
      }
    }
  }
}
</style>
